// 「邀请访客」 (members only; office/guests.ts, routes/guests.ts): make an invite link for this office (有效期 1 小时 /
// 今天 / 7 天, 次数 1 / 5 / 20 / 不限, an optional 备注), copy it, and see the live invites (who made each, uses, expiry,
// guests aboard through it) with 作废, which also sends those guests off at once. Opened from 设置 → 邀请访客 and from
// the online list. A link is shown once, right after it's made: the office keeps only a hash of it.
// Everything shown is set as text (labels and names come from other people).
import { icon, label, put } from '/icons.js';
import { t, has, fmtDate } from '/i18n.js';

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const CSS = `
#guestDlg{position:fixed;inset:0;z-index:90;display:flex;align-items:center;justify-content:center;background:rgba(20,40,60,.55)}
#guestDlg .gcard{background:#fff8e7;border:3px solid #1b1410;border-radius:18px;padding:18px 20px;width:420px;max-width:calc(100vw - 24px);max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);overflow:auto;box-sizing:border-box;font:14px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;text-align:left}
#guestDlg h3{margin:0 0 4px;display:flex;align-items:center;gap:8px} #guestDlg h3 .x{margin-left:auto}
#guestDlg h4{margin:14px 0 6px;font-size:14px}
#guestDlg .note{font-size:12px;color:#666;margin:0 0 8px}
#guestDlg .seg{display:flex;gap:6px;flex-wrap:wrap;margin:4px 0 8px}
#guestDlg .seg button,#guestDlg .btn{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:3px 12px;background:#fff;cursor:pointer}
#guestDlg .seg button.on{background:#FDD03B}
#guestDlg .btn.main{background:#FDD03B;font-weight:600;padding:6px 16px}
#guestDlg .btn.warn{background:#fee2e2;padding:2px 10px;font-size:12px}
#guestDlg .x{font:inherit;border:2px solid #1b1410;border-radius:999px;background:#fff;cursor:pointer;padding:0 6px}
#guestDlg label.f{font-size:12px;font-weight:600;display:block}
#guestDlg input[type=text]{font:inherit;width:100%;box-sizing:border-box;padding:6px 8px;border:2px solid #1b1410;border-radius:8px}
#guestDlg .made{background:#fff;border:2px dashed #1b1410;border-radius:10px;padding:8px;margin:10px 0 0}
#guestDlg .made .ln{display:flex;gap:6px} #guestDlg .made input{font-size:12px} #guestDlg .made .btn{white-space:nowrap}
#guestDlg .list .it{display:flex;gap:8px;align-items:center;border-top:1px solid #e5d7b8;padding:6px 0}
#guestDlg .list .it .t{flex:1;min-width:0} #guestDlg .list .it .lb{font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#guestDlg .list .it .meta{font-size:12px;color:#666}
#guestDlg .list .empty{font-size:12px;color:#888}
#guestDlg .err{color:#b91c1c;font-size:12px;min-height:1em;margin:6px 0 0}`;

/** The words for the office's refusal codes (office/guests.ts): guests.err.<code>; null for one this page doesn't know. */
const WHY = (c) => (c && has(`guests.err.${c}`) ? t(`guests.err.${c}`) : null);
const fmt = (ts) => fmtDate(ts, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

/**
 * @param {object} o
 * @param {string} o.room this office's room name (the /ws/<room> the page connects to)
 * @param {(text: string) => void} o.toast
 * @param {Function} o.addSettingsSection public/settings.js
 */
export function initGuests({ room, toast, addSettingsSection }) {
  if (!document.getElementById('guest-css')) { const s = el('style'); s.id = 'guest-css'; s.textContent = CSS; document.head.append(s); }
  addSettingsSection({
    id: 'guests', title: () => t('guests.title'), icon: 'people', order: 25,
    render(box) {
      box.append(el('p', '', t('guests.intro')));
      const b = label(el('button', 'btn'), 'people', t('guests.open')); b.type = 'button'; b.onclick = open; box.append(b);
    },
  });

  let dlg = null, ttl = '1h', max = 1;
  /** 有效期 and 可用次数's choices: the value sent, its words. */
  const TTLS = [['1h', 'guests.ttl.hour'], ['today', 'guests.ttl.today'], ['7d', 'guests.ttl.week']], MAXES = [1, 5, 20, 0];
  const api = async (path, body) => {
    const r = await fetch(path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ room, ...body }) } : {});
    return r.json().catch(() => ({ ok: false }));
  };
  function close() { dlg?.remove(); dlg = null; removeEventListener('keydown', esc, true); }
  function esc(e) { if (e.key === 'Escape') { e.stopImmediatePropagation(); close(); } }

  function seg(opts, get, set) {
    const s = el('div', 'seg');
    for (const [v, text] of opts) {
      const b = el('button', v === get() ? 'on' : '', text); b.type = 'button';
      b.onclick = () => { set(v); for (const x of s.children) x.classList.toggle('on', x === b); };
      s.append(b);
    }
    return s;
  }

  async function open() {
    close();
    dlg = el('div'); dlg.id = 'guestDlg'; dlg.setAttribute('role', 'dialog'); dlg.setAttribute('aria-label', t('guests.title'));
    const card = el('div', 'gcard'); dlg.append(card);
    dlg.addEventListener('pointerdown', (e) => { if (e.target === dlg) close(); });
    dlg.addEventListener('keydown', (e) => e.stopPropagation()); // typing here never walks the otter
    addEventListener('keydown', esc, true);
    const h = put(el('h3'), 'people', t('guests.title'));
    const x = el('button', 'x'); x.type = 'button'; x.title = t('guests.close'); x.append(icon('close')); x.onclick = close; h.append(x);
    card.append(h, el('p', 'note', t('guest.invite.scope', { play: t('floors.name.play') })));

    card.append(el('label', 'f', t('guests.ttl')), seg(TTLS.map(([v, k]) => [v, t(k)]), () => ttl, (v) => (ttl = v)));
    card.append(el('label', 'f', t('guests.max')), seg(MAXES.map((n) => [n, n ? t('guests.max.n', { n }) : t('guests.max.any')]), () => max, (v) => (max = v)));
    const lab = el('input'); lab.type = 'text'; lab.maxLength = 24; lab.placeholder = t('guests.label.placeholder');
    card.append(el('label', 'f', t('guests.label')), lab);
    const make = el('button', 'btn main', t('guests.make')); make.type = 'button'; make.style.marginTop = '10px';
    const err = el('p', 'err'), made = el('div');
    card.append(make, err, made);
    const list = el('div', 'list');
    card.append(el('h4', '', t('guests.live')), list);
    document.body.append(dlg);
    lab.focus();

    make.onclick = async () => {
      make.disabled = true; err.textContent = '';
      const end = new Date(); end.setHours(23, 59, 59, 999);
      const j = await api('/api/guests', { ttl, max, label: lab.value.trim(), ...(ttl === 'today' && { until: end.getTime() }) }).catch(() => null);
      make.disabled = false;
      if (!j?.ok) { err.textContent = WHY(j?.code) || t('guests.makeFailed'); return; }
      // s: the Raft server whose office this is (the office's own server: routes/guests.ts answers it)
      const url = `${location.origin}/guest#o=${encodeURIComponent(room)}${j.srv ? `&s=${encodeURIComponent(j.srv)}` : ''}&i=${encodeURIComponent(j.id)}&t=${encodeURIComponent(j.t || '')}`;
      const box = el('div', 'made'), ln = el('div', 'ln'), inp = el('input'); inp.type = 'text'; inp.readOnly = true; inp.value = url;
      const cp = el('button', 'btn', t('guests.copy')); cp.type = 'button';
      cp.onclick = async () => { try { await navigator.clipboard.writeText(url); cp.textContent = t('guests.copied'); } catch { inp.select(); document.execCommand?.('copy'); cp.textContent = t('guests.copied'); } };
      ln.append(inp, cp);
      box.append(ln, el('p', 'note', t('guests.made', { exp: fmt(j.exp) })));
      made.replaceChildren(box); inp.select(); lab.value = '';
      refresh(list);
    };
    refresh(list);
  }

  async function refresh(list) {
    const j = await api(`/api/guests?room=${encodeURIComponent(room)}`).catch(() => null);
    if (!list.isConnected) return;
    list.replaceChildren();
    if (!j?.ok) { list.append(el('p', 'empty', t('guests.listFailed'))); return; }
    if (!j.invites.length) { list.append(el('p', 'empty', t('guests.none'))); return; }
    for (const i of j.invites) {
      const it = el('div', 'it'), tx = el('div', 't');
      tx.append(el('div', 'lb', i.label || t('guests.noLabel')));
      const uses = i.max ? t('guests.uses.max', { uses: i.uses, max: i.max }) : t('guests.uses.n', { uses: i.uses });
      const by = i.mine ? t('guests.me') : i.by;
      tx.append(el('div', 'meta', i.here ? t('guests.metaHere', { by, uses, exp: fmt(i.exp), here: i.here }) : t('guests.meta', { by, uses, exp: fmt(i.exp) })));
      const rv = el('button', 'btn warn', t('guests.revoke')); rv.type = 'button';
      rv.onclick = async () => {
        if (!confirm(i.here ? t('guests.revoke.confirmHere', { n: i.here }) : t('guests.revoke.confirm'))) return;
        rv.disabled = true;
        const r = await api('/api/guests/revoke', { rid: i.rid }).catch(() => null);
        if (r?.ok) toast(t('guests.revoked')); else toast(WHY(r?.code) || t('guests.revokeFailed'));
        refresh(list);
      };
      it.append(tx, rv); list.append(it);
    }
  }

  return { open };
}
